import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { Fragment, useMemo } from 'react'

import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import {
  matchSlugTargets,
  STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS,
} from '@src/experiments/statsig-integration/experiment-targets.types'
import type { SlugExperimentTarget } from '@src/experiments/statsig-integration/experiment-targets.types'
import { useTargetedStatsigExperiment } from '@src/experiments/statsig-integration/use-targeted-statsig-experiment'
import { BUTTON_TYPES, Button, Image } from '@src/quince-components/src'
import { renderProductOption } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { useSvdOptionRows } from '@src/quince-components/src/atoms/productForm/use-svd-option-rows'
import SiblingProductOptions, { SiblingProductOptionType } from '@src/quince-components/src/atoms/siblingProductOptions'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { ProductOptionDisplayType, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import type { SvdSelection } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { resolveSelectedVariant } from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import useSizeGuideSvdExperiment from '@src/quince-site-common/src/hooks/use-size-guide-svd-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import BundleProductCardPriceAndTitleV2Legacy from './bundle-product-card-price-and-title-v2-legacy'
import styles from './BundleProductCard.module.scss'

import type { BundleProductCardProps } from './bundle-product-card-types'

const OPTION_DISPLAY_TYPES_VS_CLASS_NAME_NEW_DESIGN_V2 = {
  [ProductOptionDisplayType.TEXT]: styles.textOptionLabelClassname_newDesignV2,
}

/** Stable empty literals — module-scope so the SVD branches never re-arm memos on a new reference. */
// Typed off the helper's own return so the SVD branch can't widen `productOptions` into a union.
const EMPTY_PRODUCT_OPTIONS: ReturnType<typeof productHelper.getCascadingProductOptions> = []
const EMPTY_SVD_SELECTED: SvdSelection[] = []

function BundleProductCardLegacy({
  product,
  selectedOptions,
  salePrice,
  discount,
  isAddOnProduct = false,
  expanded = true,
  selectedSiblingProductId,
  error = null,
  onSiblingOptionSelect,
  setExpanded,
  onOptionSelect,
  showSizeChart,
  isSvd = false,
  svdSelected,
  serverVariantId,
  onSvdOptionSelect,
  'data-testid': dataTestId,
}: BundleProductCardProps) {
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('pdp')
  const { t: tCommon } = useTranslation('common')
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const discountedSalePrice = useMemo(() => {
    return getDiscountedPrice(salePrice, discount)
  }, [salePrice, discount, selectedOptions])

  const { experimentBucket: imageExperimentBucket } = useTargetedStatsigExperiment(
    STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
    STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS[STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA],
    (targets: SlugExperimentTarget[]) => matchSlugTargets(targets, product.slug),
  )

  // SVD carries no legacy `productOptions`; its rows come from `svdOptionRows` below.
  const productOptions = isSvd
    ? EMPTY_PRODUCT_OPTIONS
    : productHelper.getCascadingProductOptions(product, selectedOptions)
  const optionNameSelectedValueMap = selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const chooseProductText = isAddOnProduct ? `${tCommon('add_a')} ${product.title}` : product.title

  // Per-card gate. The `show` bucket of PDP_SIZE_GUIDE_SVD puts this constituent on the V3 guide, which
  // renders from `product.sizeGuide` alone — so a constituent without one has nothing to open and hides
  // its button (just that card, never the others). The `control` bucket keeps the legacy size-chart
  // behaviour untouched. The bucket is only read when the card has some size data at stake, so products
  // that would show no button either way log no Statsig exposure.
  const hasSizeGuide = !!product.sizeGuide
  const hasSizeChart = Boolean(product.sizeChart || product.mergedSizeChart)
  const isSizeGuideSvd = useSizeGuideSvdExperiment(hasSizeGuide || hasSizeChart)
  const shouldShowSizeChartButton = isSizeGuideSvd ? hasSizeGuide : hasSizeChart

  // SVD VDA rows for this constituent — the same builder the single-product buy box uses, so swatch /
  // measurement / select rows, cascade availability and DS-vs-legacy routing all match the PDP exactly.
  // `enabled: false` on a legacy card returns `[]` without touching any SVD helper.
  const svdOptionRows = useSvdOptionRows({
    enabled: isSvd,
    product: product as unknown as SvdProduct,
    svdSelected: svdSelected ?? EMPTY_SVD_SELECTED,
    onOptionSelect: onSvdOptionSelect ?? EMPTY_ARROW_FUNC,
    // Engraving is a buy-box concern — bundle cards are selection-only (V1 parity).
    includeEngraving: false,
    productFormOptionsClass: styles.productOptions,
    legendClassName: styles.productOptionsLegend,
    optionSelectorClassName: styles.productOptionsSelector,
    t: tCommon,
  })

  const productMedia = useMemo(() => {
    // SVD resolves its thumbnail off the variant (below), and an SVD product carries no legacy `images`.
    if (isSvd) return []
    const experimentImages =
      imageExperimentBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_A ||
      imageExperimentBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_B
        ? productHelper.getImageExperimentForOptions(product, selectedOptions, {
            bucket: imageExperimentBucket,
            name: STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
          })
        : undefined
    return productHelper.getImagesForOptions(product, selectedOptions, undefined, experimentImages)
  }, [product, selectedOptions, imageExperimentBucket, isSvd])

  // SVD thumbnail: the resolved variant's hero image (server-resolved variant first, then min-price) —
  // keeps the card image consistent with the price shown beside it.
  const imageUrl = isSvd
    ? (resolveSelectedVariant(product, svdSelected ?? EMPTY_SVD_SELECTED, serverVariantId)?.heroImageUrl ?? '')
    : productMedia.length
      ? (productMedia[0]?.image.url ?? '')
      : ''

  const renderBundleProductDetailsSection = () => {
    const productOptionsRenderer = productOptions
      .filter(po => !po.isHidden)
      .map(productOption => ({
        node: renderProductOption({
          productOption: productOption,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          onOptionSelect: onOptionSelect,
          hideSizeChart: true,
          sizeChart: undefined,
          handleSizeChart: EMPTY_ARROW_FUNC,
          product: product,
          selectedOptions: selectedOptions,
          optionsContainerClassName: styles.productOptions,
          mergedSizeChart: undefined,
          legendClassName: styles.productOptionsLegend,
          optionSelectorClassName: styles.productOptionsSelector,
          optionDisplayTypesVsClassName: OPTION_DISPLAY_TYPES_VS_CLASS_NAME_NEW_DESIGN_V2,
          t: tCommon,
          noMarginForSizeOption: productOption.name === ProductOptionName.Size,
          isBundle: true,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }))

    const addOnOptionsRenderer = product.addOnProductOptionGroups?.map(group => {
      return {
        node: (
          <SiblingProductOptions
            siblingProductOptionGroup={group}
            displayType={onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
            selectedProductId={selectedSiblingProductId}
            onSelect={onSiblingOptionSelect}
            optionSelectorClassName={classNames(
              styles.siblingOptionsSelector,
              styles.siblingOptionsSelector_adjustedPadding,
              styles.productOptionsSelector_newDesignV2,
            )}
            className={classNames(styles.siblingOptions, styles.siblingOptions_newDesignV2)}
            labelClassName={styles.siblingOptionsLabel_newDesignV2}
          />
        ),
        displaySequence: group.optionDisplaySequence,
        key: group.groupId,
      }
    })

    // SVD swaps the legacy option rows for the VDA rows; merge (sibling) rows are shared by both flows —
    // V2 still carries `addOnProductOptionGroups` for set-type merges.
    const combinedProductOptions = [
      ...(addOnOptionsRenderer || []),
      ...(isSvd ? svdOptionRows : productOptionsRenderer),
    ]
    combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
    return (
      expanded && (
        <>
          <div className={classNames(styles.productOptionsContainer, styles.productOptionsContainer_newDesignV2)}>
            {combinedProductOptions.map(option => (
              <Fragment key={option.key}>{option.node}</Fragment>
            ))}
          </div>
          {shouldShowSizeChartButton && showSizeChart && (
            <div className={styles.sizeChart}>
              <Button type={BUTTON_TYPES.SECONDARY_LINK} onClick={showSizeChart} className={styles.sizeChart}>
                {t('size_chart')}
              </Button>
            </div>
          )}
          {error && <div className={styles.errorMessage}>{error}</div>}
        </>
      )
    )
  }

  return (
    <>
      <div
        data-testid={dataTestId}
        className={classNames(
          styles.bundleCardWrapper,
          isAddOnProduct && styles.addOnBundle,
          styles.bundleCardWrapper_newDesignV2,
        )}
      >
        <div className={styles.bundleCard}>
          <div
            className={classNames(
              styles.imageContainer,
              isAddOnProduct && (isMobile || !expanded) && styles.addOnProductImageContainer,
              styles.imageContainer_newDesignV2,
            )}
          >
            <Image url={imageUrl} title={product.title} maxWidth={250} overrideMaxImageWidth1600 alt={product.title} />
          </div>
          <div className={styles.productDetails}>
            <BundleProductCardPriceAndTitleV2Legacy
              chooseProductText={chooseProductText}
              discountedSalePrice={discountedSalePrice}
              salePrice={salePrice}
              isAddOnProduct={isAddOnProduct}
              expanded={expanded}
              setExpanded={setExpanded || EMPTY_ARROW_FUNC}
            />

            {!isMobile && renderBundleProductDetailsSection()}
          </div>
        </div>
        {isMobile && renderBundleProductDetailsSection()}
      </div>
    </>
  )
}

export default BundleProductCardLegacy
